import { Box, Button, CircularProgress, Grid, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { downloadDetailedList } from 'queries/downloadQueries';
import { ErrorAlert } from '../../components/atoms/Alert-message';
import PageHeader from '../../components/shared/PageHeader';
import { APIHeader } from '../../index';
import { errorNotification } from '../../reducers/handle-api-status/slice';
import {
  ListFilterTableGrid,
  homeButtonComponent,
  modernContentSection,
} from '../../styles';
import { isEmptyObject, stripTrailingSlash } from '../../utils';
import LeftPanel from '../ListPage/LeftPanel';
import ListTable from '../ListTableAndTrackingFIle/ListTable';
import MySavedViewModal from '../MySavedViewModal';

//Detailed List Page Component
const DetailedList = props => {
  const { apiConfig, productFamilyList, userInfo, errorInAPI } = props;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const bookmarkRef = useRef(false);
  const dispatch = useDispatch();

  const originURL = window.location.origin.toString();
  const filterId = stripTrailingSlash(window.location.toString())
    .split('/')
    .pop();

  const [listData, setListData] = useState([]);
  const [siteListData, setSiteListData] = useState([]);
  const [statusListData, setStatusListData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openSaveModal, setOpenSaveModal] = useState(false);
  const [searchString, setSearchString] = useState('');
  const [filterName, setFilterName] = useState('');
  const [getAllFilters, setGetAllFilters] = useState([]);
  const [selectedFilterOption, setSelectedFilterOption] = useState({});
  const [filterProdFamData, setFilterProdFamData] = useState([]);
  const [filterSiteData, setFilterSiteData] = useState([]);
  const [filterStatusData, setFilterStatusData] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [columnOrderData, setColumnOrderData] = useState({
    columnId: '',
    order: '',
  });
  const [isDownloading, setIsDownloading] = useState(false);
  const [showLeftPanel, setShowLeftPanel] = useState(true);
  const { mutate: downloadList } = useMutation({
    mutationFn: downloadDetailedList,
    onSuccess: () => {
      setIsDownloading(false);
    },
    onError: error => {
      setIsDownloading(false);
      console.error('Error downloading detailed list', error);
      dispatch(
        errorNotification({
          hasErrorOccured: true,
          message: t('error_message'),
        })
      );
    },
  });

  useEffect(() => {
    //Get the site list
    fetch(`${apiConfig.uiURL}api/SiteList`, {
      headers: APIHeader(),
    })
      .then(async response => {
        const data = await response.json();
        setSiteListData(data);
        //map site data and set checked as true
        const filterData =
          data &&
          data.length > 0 &&
          data.map(siteList => {
            return {
              ...siteList,
              checked: true,
            };
          });
        setFilterSiteData(filterData);
        //Get the status list
        fetch(`${apiConfig.uiURL}api/FetchListStatus`, {
          headers: APIHeader(),
        })
          .then(async response => {
            const data = await response.json();
            setStatusListData(data);
            //map status data which and set checked as true
            const filterData =
              data &&
              data.length > 0 &&
              data.map(statusList => {
                return {
                  ...statusList,
                  checked:
                    statusList.Status !== 'Complete' &&
                    statusList.Status !== 'Cancelled',
                };
              });
            setFilterStatusData(filterData);
          })
          .catch(() => {
            dispatch(
              errorNotification({
                hasErrorOccured: true,
                message: t('error_message'),
              })
            );
          });
      })
      .catch(() => {
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiConfig]);

  //useEffect is getting exceuted for default page
  useEffect(() => {
    if (
      filterId === 'DetailedList' &&
      filterProdFamData.length > 0 &&
      filterStatusData.length > 0 &&
      filterSiteData.length > 0
    ) {
      setIsLoading(true);
      const filterProdFam = filterProdFamData
        .filter(item => item.checked)
        .map(item => item.Name)
        .toString();
      const filterSite = filterSiteData
        .filter(item => item.checked)
        .map(item => item.SiteName)
        .toString();
      const filterStatus = filterStatusData
        .filter(item => item.checked)
        .map(item => item.Status)
        .toString();

      setSelectedFilterOption({
        ProductFilter: filterProdFam,
        SitesFilter: filterSite,
        StatusFilter: filterStatus,
      });
      //Get the List of PECR to show by default
      fetch(`${apiConfig.uiURL}api/ReportforPECRList`, {
        method: 'POST',
        headers: APIHeader(),
        body: JSON.stringify({
          sort: '',
          ascdesc: '',
          filterStatus: filterStatus,
          filterSites: filterSite,
          filterProductFamily: filterProdFam,
          search: searchString,
        }),
      })
        .then(async response => {
          const data = await response.json();
          setListData(data);
          setIsLoading(false);
        })
        .catch(() => {
          dispatch(
            errorNotification({
              hasErrorOccured: true,
              message: t('error_message'),
            })
          );
        });
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterProdFamData,
    filterStatusData,
    filterSiteData,
    apiConfig,
    filterId,
  ]);

  useEffect(() => {
    if (userInfo && userInfo.userID) {
      //fetch all saved filters based on userID
      fetch(`${apiConfig.uiURL}api/FetchReportFilterBasedOnID`, {
        method: 'POST',
        headers: APIHeader(),
        body: JSON.stringify({
          UserId: userInfo.userID,
        }),
      })
        .then(async response => {
          const data = await response.json();
          setGetAllFilters(data);
        })
        .catch(() => {
          dispatch(
            errorNotification({
              hasErrorOccured: true,
              message: t('error_message'),
            })
          );
        });
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo, userInfo.userID, apiConfig]);

  useEffect(() => {
    //map product family and set checked as true
    if (productFamilyList && productFamilyList.length > 0) {
      const filterData = productFamilyList.map(productFamilyList => {
        return {
          ...productFamilyList,
          checked: true,
        };
      });
      setFilterProdFamData(filterData);
    }
  }, [productFamilyList]);
  //To load the page with bookmarked filter
  useEffect(() => {
    if (
      bookmarkRef.current ||
      filterId === 'DetailedList' ||
      getAllFilters.length === 0 ||
      productFamilyList.length === 0 ||
      statusListData.length === 0 ||
      siteListData.length === 0
    ) {
      return;
    }
    bookmarkRef.current = true;
    setIsLoading(true);
    const findFilter = getAllFilters.find(list => {
      return filterId.toString() === list.ID.toString();
    });
    setSelectedFilterOption(findFilter);

    const findProdFamFiltered = findFilter.ProductFilter.split(',');
    const findStatusFiltered = findFilter.StatusFilter.split(',');
    const findSiteFiltered = findFilter.SitesFilter.split(',');

    // Get the List of PECR based on selected filter
    fetch(`${apiConfig.uiURL}api/ReportforPECRList`, {
      method: 'POST',
      headers: APIHeader(),
      body: JSON.stringify({
        sort: findFilter.SortBy,
        ascdesc: findFilter.OrderBy,
        filterStatus: findFilter.StatusFilter,
        filterSites: findFilter.SitesFilter,
        filterProductFamily: findFilter.ProductFilter,
        // search: findFilter,
      }),
    })
      .then(async response => {
        const data = await response.json();
        setListData(data);
        const prodFamFiltered = productFamilyList.map(item => {
          if (findProdFamFiltered.includes(item.Name)) {
            return { ...item, checked: true };
          } else {
            return { ...item, checked: false };
          }
        });
        setFilterProdFamData(prodFamFiltered);

        const siteFiltered = siteListData.map(item => {
          if (findSiteFiltered.includes(item.SiteName)) {
            return { ...item, checked: true };
          } else {
            return { ...item, checked: false };
          }
        });
        setFilterSiteData(siteFiltered);

        const statusFiltered = statusListData.map(item => {
          if (findStatusFiltered.includes(item.Status)) {
            return { ...item, checked: true };
          } else {
            return { ...item, checked: false };
          }
        });
        setFilterStatusData(statusFiltered);

        setColumnOrderData({
          columnId: findFilter.SortBy,
          order: findFilter.OrderBy,
        });
        setIsLoading(false);
      })
      .catch(() => {
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getAllFilters,
    filterId,
    productFamilyList,
    statusListData,
    siteListData,
    listData,
    apiConfig,
  ]);

  //options for My Save View drop-down
  const saveViewOptions =
    getAllFilters.length > 0
      ? getAllFilters.map(list => {
          return {
            label: list.FilterName,
            value: list.ID,
          };
        })
      : [];

  const navigateToTrackingPage = pecrId => {
    //navigating to tracking page based on pecrId
    navigate(`/TrackingFile?id=${pecrId}`);
  };

  //To handle the onChange event of search box
  const handleSearchChange = event => {
    const searchText = event.target.value.trim();
    setSearchString(searchText);
    // Get the List of PECR based on search
    fetch(`${apiConfig.uiURL}api/ReportforPECRList`, {
      method: 'POST',
      headers: APIHeader(),
      body: JSON.stringify({
        search: searchText,
        filterStatus: selectedFilterOption.StatusFilter,
        filterSites: selectedFilterOption.SitesFilter,
        filterProductFamily: selectedFilterOption.ProductFilter,
        sort:
          selectedFilterOption && selectedFilterOption.SortBy
            ? selectedFilterOption.SortBy
            : 'ID',
        ascdesc:
          selectedFilterOption && selectedFilterOption.OrderBy
            ? selectedFilterOption.OrderBy
            : 'desc',
      }),
    })
      .then(async response => {
        const data = await response.json();
        setListData(data);
      })
      .catch(() => {
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });
  };

  //To handle the refresh button of Filter
  const onRefreshClick = filteredData => {
    // Get the List of PECR based on selected filter
    fetch(`${apiConfig.uiURL}api/ReportforPECRList`, {
      method: 'POST',
      headers: APIHeader(),
      body: JSON.stringify({
        filterStatus: filteredData.selectedStatus,
        filterSites: filteredData.selectedSites,
        filterProductFamily: filteredData.selectedProductFamily,
        search: searchString,
        sort:
          selectedFilterOption && selectedFilterOption.SortBy
            ? selectedFilterOption.SortBy
            : 'ID',
        ascdesc:
          selectedFilterOption && selectedFilterOption.OrderBy
            ? selectedFilterOption.OrderBy
            : 'desc',
      }),
    })
      .then(async response => {
        const data = await response.json();
        setListData(data);
        setSelectedFilterOption({
          ...selectedFilterOption,
          ProductFilter: filteredData.selectedProductFamily,
          SitesFilter: filteredData.selectedSites,
          StatusFilter: filteredData.selectedStatus,
        });
      })
      .catch(() => {
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });
  };

  //To handle the sorting of columns in List table
  const handleOrderChange = (columnId, order) => {
    setColumnOrderData({
      columnId: columnId,
      order: order,
    });
    // Get the List of PECR based on sorted column and order
    fetch(`${apiConfig.uiURL}api/ReportforPECRList`, {
      method: 'POST',
      headers: APIHeader(),
      body: JSON.stringify({
        sort: columnId,
        ascdesc: order,
        filterStatus: selectedFilterOption.StatusFilter,
        filterSites: selectedFilterOption.SitesFilter,
        filterProductFamily: selectedFilterOption.ProductFilter,
        search: searchString,
      }),
    })
      .then(async response => {
        const data = await response.json();
        setListData(data);
        setSelectedFilterOption({
          ...selectedFilterOption,
          SortBy: columnId,
          OrderBy: order,
        });
      })
      .catch(() => {
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });
  };

  //To handle the onclick event of Save icon
  const onSaveIconClick = () => {
    setOpenSaveModal(true);
  };

  //To handle the close/cancel click of saved view Name Modal
  const onCloseModalClick = () => {
    setOpenSaveModal(false);
    setErrorMessage(null);
  };

  //To handle the Save click of Saved View Name Modal
  const onSaveModalClick = () => {
    setErrorMessage(null);
    if (userInfo && userInfo.userID && userInfo.userID !== '') {
      //Call InsertUpdatePECRFilter API to create new filter with new name and other selected parameters
      fetch(`${apiConfig.uiURL}api/InsertUpdateReportFilter`, {
        method: 'POST',
        headers: APIHeader(),
        body: JSON.stringify({
          FilterId: null,
          UserId: userInfo.userID,
          sortBy:
            selectedFilterOption && selectedFilterOption.SortBy
              ? selectedFilterOption.SortBy
              : 'ID',
          orderBy:
            selectedFilterOption && selectedFilterOption.OrderBy
              ? selectedFilterOption.OrderBy
              : 'desc',
          statusFilter: selectedFilterOption.StatusFilter,
          siteFilter: selectedFilterOption.SitesFilter,
          productFilter: selectedFilterOption.ProductFilter,
          filterName: filterName,
        }),
      })
        .then(async response => {
          const insertedFilterId = await response.json();
          //To close the Saved View Name Modal
          setOpenSaveModal(false);

          if (userInfo && userInfo.userID) {
            //fetch all saved filters based on userID
            fetch(`${apiConfig.uiURL}api/FetchReportFilterBasedOnID`, {
              method: 'POST',
              headers: APIHeader(),
              body: JSON.stringify({
                UserId: userInfo.userID,
              }),
            }).then(async response => {
              const data = await response.json();
              setGetAllFilters(data);
              //find selected filter based on last inserted Id/filter
              const findSelectedFilter = data.find(item => {
                return (
                  item.ID.toString() === insertedFilterId.insertId.toString()
                );
              });
              setSelectedFilterOption(findSelectedFilter);
              replaceURL(insertedFilterId.insertId);
            });
          }
        })
        .catch(() => {
          dispatch(
            errorNotification({
              hasErrorOccured: true,
              message: t('error_message'),
            })
          );
        });
    }
  };

  //handle onchange event of My saved view drop-down
  const handleSavedViewChange = event => {
    const findFilter = getAllFilters.find(list => {
      return event.value === list.ID;
    });
    setSelectedFilterOption(findFilter);
    const findProdFamFiltered = findFilter.ProductFilter.split(',');
    const findStatusFiltered = findFilter.StatusFilter.split(',');
    const findSiteFiltered = findFilter.SitesFilter.split(',');
    // Get the List of PECR based on selected filter
    fetch(`${apiConfig.uiURL}api/ReportforPECRList`, {
      method: 'POST',
      headers: APIHeader(),
      body: JSON.stringify({
        sort: findFilter.SortBy,
        ascdesc: findFilter.OrderBy,
        filterStatus: findFilter.StatusFilter,
        filterSites: findFilter.SitesFilter,
        filterProductFamily: findFilter.ProductFilter,
        // search: findFilter,
      }),
    })
      .then(async response => {
        const data = await response.json();
        setListData(data);
        const prodFamFiltered = productFamilyList.map(item => {
          if (findProdFamFiltered.includes(item.Name)) {
            return { ...item, checked: true };
          } else {
            return { ...item, checked: false };
          }
        });
        setFilterProdFamData(prodFamFiltered);

        const siteFiltered = siteListData.map(item => {
          if (findSiteFiltered.includes(item.SiteName)) {
            return { ...item, checked: true };
          } else {
            return { ...item, checked: false };
          }
        });
        setFilterSiteData(siteFiltered);

        const statusFiltered = statusListData.map(item => {
          if (findStatusFiltered.includes(item.Status)) {
            return { ...item, checked: true };
          } else {
            return { ...item, checked: false };
          }
        });
        setFilterStatusData(statusFiltered);

        setColumnOrderData({
          columnId: findFilter.SortBy,
          order: findFilter.OrderBy,
        });
        replaceURL(event.value);
      })
      .catch(() => {
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });
  };

  //To replace the URL with originURL and the selected filterId
  const replaceURL = value => {
    const finalURL = `${originURL}/DetailedList/${value}`;
    window.history.replaceState(null, null, finalURL);
  };

  //to get the value for My Saved View drop down
  const valueAfterFilterSelection =
    saveViewOptions.length > 0 &&
    selectedFilterOption &&
    !isEmptyObject(selectedFilterOption) &&
    selectedFilterOption.ID &&
    saveViewOptions.filter(
      ({ value }) => selectedFilterOption.ID.toString() === value.toString()
    );

  //To download the files on DownloadExcelforPECRList API click
  const onDownloadExcelClick = () => {
    setIsDownloading(true);
    downloadList({
      search: searchString,
      sort: selectedFilterOption.SortBy ? selectedFilterOption.SortBy : 'ID',
      ascdesc: selectedFilterOption.OrderBy
        ? selectedFilterOption.OrderBy
        : 'desc',
      filterStatus: selectedFilterOption.StatusFilter,
      filterSites: selectedFilterOption.SitesFilter,
      filterProductFamily: selectedFilterOption.ProductFilter,
    });
  };

  const onMenuIconClick = () => {
    setShowLeftPanel(!showLeftPanel);
  };

  if (errorInAPI.hasErrorOccured) {
    return <ErrorAlert message={errorInAPI.message} />;
  }

  return (
    <div className="flex flex-col">
      <PageHeader title={t('PECR_List_Table')}>
        <Button
          onClick={onDownloadExcelClick}
          sx={{
            textTransform: 'none',
            fontSize: '16px',
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
            },
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            color: 'white',
          }}
          disabled={isDownloading}
        >
          {t('Download_Excel')}
        </Button>
      </PageHeader>
      <Box style={modernContentSection}>
        {isLoading ? (
          <CircularProgress />
        ) : (
          <Grid style={ListFilterTableGrid}>
            {showLeftPanel && (
              <LeftPanel
                handleSearchChange={handleSearchChange}
                handleSavedViewChange={handleSavedViewChange}
                onRefreshClick={onRefreshClick}
                onSaveIconClick={onSaveIconClick}
                saveViewOptions={saveViewOptions}
                valueAfterFilterSelection={valueAfterFilterSelection}
                filterProdFamData={filterProdFamData}
                filterSiteData={filterSiteData}
                filterStatusData={filterStatusData}
              />
            )}
            {listData.length > 0 ? (
              <Grid>
                <ListTable
                  rows={listData}
                  navigateToTrackingPage={navigateToTrackingPage}
                  orderHandler={handleOrderChange}
                  OrderSortBy={columnOrderData}
                  isDetailedListPage={true}
                  showLeftPanel={showLeftPanel}
                />
              </Grid>
            ) : (
              <Grid style={homeButtonComponent}>
                <Typography style={{ padding: '10px', fontWeight: 'bold' }}>
                  {t('No_result_found')}
                </Typography>
              </Grid>
            )}
          </Grid>
        )}
      </Box>

      <MySavedViewModal
        openSaveModal={openSaveModal}
        onCloseModalClick={onCloseModalClick}
        errorMessage={errorMessage}
        setFilterName={setFilterName}
        getAllFilters={getAllFilters || []}
        setErrorMessage={setErrorMessage}
        onSaveModalClick={onSaveModalClick}
        filterName={filterName}
        userId={userInfo.userID}
      />
    </div>
  );
};

const mapStateToProps = (state = {}) => {
  return {
    userInfo: state.userInfo,
    productFamilyList: state.pecrInfo.pecrData.productFamilyList,
    errorInAPI: state.errorNotification,
  };
};

export default connect(mapStateToProps, null)(DetailedList);
